import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import Utils3D from '../../utils/utils3D.js';
import Utils from '../utils.js';
import Spline from '../spline.js';
import GeoUtils from '../geoUtils.js';

const WORLD_AXES = {
	'x': new BABYLON.Vector3(1, 0, 0),
	'y': new BABYLON.Vector3(0, 1, 0),
	'z': new BABYLON.Vector3(0, 0, 1),
};

export default class NodeTransformV2 extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'geo', null);

		this.addInput(PARAM_TYPE.FLOAT, 'move_x', 0.0, { minValue: -3, maxValue: 3, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'move_y', 0.0, { minValue: -3, maxValue: 3, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'move_z', 0.0, { minValue: -3, maxValue: 3, units: PARAM_UNITS.METERS });

		this.addInput(PARAM_TYPE.FLOAT, 'rotate_x', 0.0, { minValue: -180, maxValue: 180, units: PARAM_UNITS.DEGREES });
		this.addInput(PARAM_TYPE.FLOAT, 'rotate_y', 0.0, { minValue: -180, maxValue: 180, units: PARAM_UNITS.DEGREES });
		this.addInput(PARAM_TYPE.FLOAT, 'rotate_z', 0.0, { minValue: -180, maxValue: 180, units: PARAM_UNITS.DEGREES });

		this.addInputsToGroup('Move & Rotate', ['move_x', 'move_y', 'move_z', 'rotate_x', 'rotate_y', 'rotate_z']);

		this.addInput(PARAM_TYPE.FLOAT, 'scale_x', 1.0, { minValue: 0.1, maxValue: 5 });
		this.addInput(PARAM_TYPE.FLOAT, 'scale_y', 1.0, { minValue: 0.1, maxValue: 5 });
		this.addInput(PARAM_TYPE.FLOAT, 'scale_z', 1.0, { minValue: 0.1, maxValue: 5 });

		this.addInput(PARAM_TYPE.BOOLEAN, 'scale_in_world_space', false);

		this.addInput(PARAM_TYPE.BOOLEAN, 'intelligent_x_scaling', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'intelligent_y_scaling', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'intelligent_z_scaling', false);

		this.addInputsToGroup('Scale', ['scale_x', 'scale_y', 'scale_z', 'scale_in_world_space', 'intelligent_x_scaling', 'intelligent_y_scaling', 'intelligent_z_scaling']);

		this.addInput(PARAM_TYPE.BOOLEAN, 'use_pivot', false);

		this.addInput(PARAM_TYPE.FLOAT, 'pivot_x', 0.0, { minValue: -3, maxValue: 3 });
		this.addInput(PARAM_TYPE.FLOAT, 'pivot_y', 0.0, { minValue: -3, maxValue: 3 });
		this.addInput(PARAM_TYPE.FLOAT, 'pivot_z', 0.0, { minValue: -3, maxValue: 3 });

		this.addInputsToGroup('Pivot', ['use_pivot', 'pivot_x', 'pivot_y', 'pivot_z']);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
		this.addOutput(PARAM_TYPE.BBOX, 'bbox');

		this.isLastResult3DCurve = false;
	}
	
	getIsInputEnabledSubclass(strInputName)
	{
		// See if have a curve or a set of meshes connected
		let bAllowCurveControls = false;
		const lastResult = this.getLastInputResult('geo');
		if(lastResult && !Utils.getIsMeshObject(lastResult[0]))
			bAllowCurveControls = true;
		if(!bAllowCurveControls)
		{
			let pInputNode = this.getInputNode('geo');
			if(pInputNode)
			{
				const strType = pInputNode.getType();
				if(strType == 'loadCurve' || strType == 'createCurve')
					bAllowCurveControls = true;
			}
		}

		if(['intelligent_y_scaling'].indexOf(strInputName) >= 0)
			return !bAllowCurveControls || this.isLastResult3DCurve;

		if(['rotate_x', 'rotate_z', 'move_y', 'scale_y', 'pivot_y'].indexOf(strInputName) >= 0)
			return !bAllowCurveControls || this.isLastResult3DCurve;

		return true;
	}

	getType()
	{ 
		return 'transform_v2';
	}
	
	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		this.isLastResult3DCurve = false;
		let pGeoList = inputValues['geo'];
		if(!pGeoList || pGeoList.length === 0)
			return null;

		if(Utils.getIsMeshObject(pGeoList[0]))
			return this.processMeshes(scene, inputValues, outputName, computedOutputResults);
		else
			return this.processCurves(scene, inputValues, outputName, computedOutputResults);
	}

	processMeshes(scene, inputValues, outputName, computedOutputResults)
	{
		let pGeoList = inputValues['geo'];

		// If we're generating the bbox output result, grab our already-computed
		// mesh result and just find its bbox:
		if(outputName === 'bbox')
			return Utils3D.getObjectsBBoxWithChildren(computedOutputResults['geo']);

		const vecWorldPivot = new BABYLON.Vector3(inputValues['pivot_x'], inputValues['pivot_y'], inputValues['pivot_z']);
		const nlrsScalingFlags = [inputValues['intelligent_x_scaling'], inputValues['intelligent_y_scaling'], inputValues['intelligent_z_scaling']];
		for(let pSrcMesh of pGeoList)
		{
			if(!pSrcMesh)
				continue;

			if(inputValues['use_pivot'])
				Utils3D.setWorldPivot(pSrcMesh, vecWorldPivot);

			if(inputValues['rotate_x'] != 0 || inputValues['rotate_y'] != 0 || inputValues['rotate_z'] != 0)
			{
				pSrcMesh.rotate(WORLD_AXES['z'], inputValues['rotate_z']/180.0*Math.PI, BABYLON.Space.WORLD);
				pSrcMesh.rotate(WORLD_AXES['x'], inputValues['rotate_x']/180.0*Math.PI, BABYLON.Space.WORLD);
				pSrcMesh.rotate(WORLD_AXES['y'], inputValues['rotate_y']/180.0*Math.PI, BABYLON.Space.WORLD);

				const pTransforms = Utils3D.getMeshWorldTransforms(pSrcMesh);
				pSrcMesh.rotationQuaternion = null;			
				let vecAngles = pTransforms.quaternion.toEulerAngles();
				pSrcMesh.rotation = vecAngles;
				pSrcMesh.scaling = pTransforms.scale;
			}

			pSrcMesh.position.x += inputValues['move_x'];
			pSrcMesh.position.y += inputValues['move_y'];
			pSrcMesh.position.z += inputValues['move_z'];

			GeoUtils.scaleMesh(pSrcMesh, inputValues['scale_x'], inputValues['scale_y'], inputValues['scale_z'], inputValues['scale_in_world_space'], nlrsScalingFlags);
		}

		return pGeoList;
	}

	processCurves(scene, inputValues, outputName, computedOutputResults)
	{
		let pSpline = new Spline(inputValues['geo']);
		this.isLastResult3DCurve = pSpline.getIs3D();

		// If we're generating the bbox output result, grab our already-computed
		// mesh result and just find its bbox:
		if(outputName === 'bbox')
		{
			let bbox = pSpline.getBBox();
			return { minimumWorld: bbox.min, maximumWorld: bbox.max };
		}

		if(pSpline.getIs3D())
		{
			const vecWorldPivot = inputValues['use_pivot'] ? new BABYLON.Vector3(inputValues['pivot_x'], inputValues['pivot_y'], inputValues['pivot_z']) : null;
			let rotateMatrix = BABYLON.Matrix.RotationYawPitchRoll(inputValues['rotate_y']/180.0*Math.PI, inputValues['rotate_x']/180.0*Math.PI, inputValues['rotate_z']/180.0*Math.PI);
			pSpline.rotate3D(rotateMatrix, vecWorldPivot);
			pSpline.scale3D(new BABYLON.Vector3(inputValues['scale_x'], inputValues['scale_y'], inputValues['scale_z']), vecWorldPivot);
			pSpline.translate3D(inputValues['move_x'], inputValues['move_y'], inputValues['move_z']);
		}
		else
		{
			const vecWorldPivot = inputValues['use_pivot'] ? new BABYLON.Vector2(inputValues['pivot_x'], inputValues['pivot_z']) : null;
			pSpline.rotate(inputValues['rotate_y'], vecWorldPivot);
			pSpline.scale(new BABYLON.Vector2(inputValues['scale_x'], inputValues['scale_z']), vecWorldPivot, inputValues['intelligent_x_scaling'], inputValues['intelligent_z_scaling']);
			pSpline.translate(inputValues['move_x'], inputValues['move_z']);
		}

		return pSpline.saveToObject();
	}

}